// FeedbackPage.tsx — public feedback board landing page over the A4 shell.
//
// Publicly readable: a signed-out visitor browses and reads everything; posting
// and upvoting prompt sign-in via useRequireAuth.
import { useMemo, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { softControlMotion } from '@/theme/interactions';
import { useToast } from '@/ui';
import { Icon } from '@/icons';
import { media } from '@/theme/media';
import { GuestBanner } from '@/components/GuestBanner';
import { useSession, useRequireAuth } from '@/hooks';
import { useFeedbackList, useFeedbackPeek, useFeedbackStats } from '@/hooks/useFeedback';
import { useRightRail, RailGroup, RailPanel } from '@/app/shell/RightRail';
import type { FeedbackCategory, FeedbackStatus } from '@/core/models/feedback';
import {
  FEEDBACK_CATEGORY_LABELS,
  FEEDBACK_CATEGORIES,
  FEEDBACK_STATUS_LABELS,
} from '@/core/models/feedback';
import { CATEGORY_ICONS, FEEDBACK_LIST_CAP, formatListCount } from './feedbackMeta';
import { FeedbackCard } from './FeedbackCard';
import { FeedbackStatsRow } from './FeedbackStatsRow';
import { SubmitFeedbackPanel } from './SubmitFeedbackPanel';
import { FeedbackBanner } from './FeedbackBanner';
import {
  FeedbackOptionalPanel,
  FeedbackInsightsPanel,
  FeedbackGivePanel,
} from './panels/FeedbackBoardPanels';

const BannerSlot = styled.div`
  margin: 12px 0;
`;

const Tabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 24px 0 14px;
  ${media.mobile`gap: 6px;`}
`;

const Tab = styled.button<{ $on: boolean }>`
  ${softControlMotion}
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 11px 14px;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.radius.md};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 600;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme, $on }) => ($on ? `${theme.color.brandFrom}22` : 'transparent')};
  color: ${({ theme, $on }) => ($on ? theme.color.brandFrom : theme.color.textPrimary)};
  &:hover {
    background-color: ${({ theme }) => `${theme.color.brandFrom}18`};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const TabCount = styled.span`
  font-variant-numeric: tabular-nums;
  opacity: 0.75;
`;

/** The "More" dropdown — styled to read as one of the tab pills (task spec
 * §7's category tab bar), holding whichever categories don't get a
 * top-level tab (Accessibility, Moderation policy, Other). */
const MoreSelect = styled.select<{ $on: boolean }>`
  ${softControlMotion}
  color-scheme: ${({ theme }) => theme.mode};
  padding: 11px 12px;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.radius.md};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 600;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme, $on }) => ($on ? `${theme.color.brandFrom}22` : 'transparent')};
  color: ${({ theme, $on }) => ($on ? theme.color.brandFrom : theme.color.textPrimary)};

  option {
    background: ${({ theme }) => theme.color.surface};
    color: ${({ theme }) => theme.color.textPrimary};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin: 0 0 14px;
  a {
    color: ${({ theme }) => theme.color.brandFrom};
    font-size: ${({ theme }) => theme.fontSize.xs};
  }
`;

const Spacer = styled.div`
  flex: 1;
`;

const Select = styled.select`
  ${softControlMotion}
  color-scheme: ${({ theme }) => theme.mode};
  padding: 11px 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme }) => theme.color.surface};
  color: ${({ theme }) => theme.color.textPrimary};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};

  option {
    background: ${({ theme }) => theme.color.surface};
    color: ${({ theme }) => theme.color.textPrimary};
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Empty = styled.div`
  padding: 28px 16px;
  text-align: center;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
  background: ${({ theme }) => (theme.cardsGlass ? 'transparent' : theme.color.surface)};
  border-radius: ${({ theme }) => theme.radius.md};
`;

/** Skeleton rows — keeps "loading" visually distinct from "genuinely empty". */
const Skeleton = styled.div`
  height: 92px;
  border-radius: ${({ theme }) => theme.radius.lg};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme }) => theme.color.surface};
  opacity: 0.55;
`;

/** 'mine' is a client-only pseudo-category (never sent as a query param) —
 * see the category tab bar below. */
type TabValue = 'all' | 'mine' | FeedbackCategory;

/** Top-level tabs, in display order — the remaining categories live behind
 * the "More" dropdown (task spec: "All / My feedback / Bugs / Feature
 * requests / Performance / UI-UX / More(dropdown: remaining categories)"). */
const TOP_LEVEL_CATEGORIES: FeedbackCategory[] = ['bug', 'feature', 'performance', 'ux'];
const MORE_CATEGORIES: FeedbackCategory[] = FEEDBACK_CATEGORIES.filter(
  (c) => !TOP_LEVEL_CATEGORIES.includes(c),
);

export function FeedbackPage() {
  const { isAuthenticated, currentUser } = useSession();
  const location = useLocation();
  const requestedCategory = new URLSearchParams(location.search).get('submit');
  // "Report playback issue" (video player, 1.27.50) arrives with a title and details filled in.
  const prefillTitle = (new URLSearchParams(location.search).get('title') ?? '').slice(0, 140);
  const prefillBody = (new URLSearchParams(location.search).get('body') ?? '').slice(0, 2000);
  const initialCategory = requestedCategory === 'feature' ? 'feature' : 'bug';
  const toast = useToast();
  const requireAuth = useRequireAuth();

  const [params, setParams] = useSearchParams();
  const requestedTab = params.get('filter');
  const tab: TabValue =
    requestedTab === 'mine' || FEEDBACK_CATEGORIES.includes(requestedTab as FeedbackCategory)
      ? (requestedTab as TabValue)
      : 'all';
  const requestedStatus = params.get('status');
  const status: FeedbackStatus | 'all' =
    requestedStatus && requestedStatus in FEEDBACK_STATUS_LABELS
      ? (requestedStatus as FeedbackStatus)
      : 'all';
  const sort: 'top' | 'new' | 'updated' =
    params.get('sort') === 'updated' ? 'updated' : params.get('sort') === 'new' ? 'new' : 'top';
  function setFilter(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value === 'all') next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  }
  const setTab = (value: TabValue) => setFilter('filter', value);
  const [submitOpen, setSubmitOpen] = useState(
    () => isAuthenticated && (requestedCategory === 'bug' || requestedCategory === 'feature'),
  );

  const { stats, loading: statsLoading } = useFeedbackStats();

  // Filter ownership on the server before pagination; keep category independent.
  const query = useMemo(
    () => ({
      ...(tab === 'mine' ? { mine: true } : tab === 'all' ? {} : { category: tab }),
      ...(status === 'all' ? {} : { status }),
      sort,
    }),
    [tab, status, sort],
  );

  const { items, loadState, submit, reconcileAfterCreate, toggleUpvote } = useFeedbackList(query);

  const myHandle = currentUser?.handle;
  const displayedItems = useMemo(() => {
    if (tab !== 'mine') return items;
    return items.filter((i) => i.author.handle === myHandle);
  }, [items, tab, myHandle]);

  function selectTab(next: TabValue) {
    if (next === 'mine' && !requireAuth('view your feedback')) return;
    setTab(next);
  }

  // Cheap approach (documented judgment call — see PR notes): counts respect
  // the active status filter but not the active category tab, computed
  // client-side from one unfiltered-by-category read rather than N requests.
  const countsQuery = useMemo(() => (status === 'all' ? {} : { status }), [status]);
  const { items: countsItems } = useFeedbackPeek(countsQuery);
  const categoryCounts = useMemo(() => {
    const counts: Partial<Record<FeedbackCategory, number>> = {};
    for (const i of countsItems) counts[i.category] = (counts[i.category] ?? 0) + 1;
    return counts;
  }, [countsItems]);
  const mineCount = useMemo(
    () => countsItems.filter((i) => i.author.handle === myHandle).length,
    [countsItems, myHandle],
  );

  const rail = useMemo(
    () => (
      <RailGroup pageKey="feedback">
        <RailPanel id="trending" label="Trending feedback">
          <FeedbackOptionalPanel kind="trending" />
        </RailPanel>
        <RailPanel id="insights" label="Feedback insights">
          <FeedbackInsightsPanel />
        </RailPanel>
        <RailPanel id="give-feedback" label="Give feedback">
          <FeedbackGivePanel
            onOpen={() => (requireAuth('share feedback') ? setSubmitOpen(true) : undefined)}
          />
        </RailPanel>
        <RailPanel id="mine" label="My feedback" defaultHidden>
          <FeedbackOptionalPanel kind="mine" />
        </RailPanel>
        <RailPanel id="recent" label="Recently updated" defaultHidden>
          <FeedbackOptionalPanel kind="recent" />
        </RailPanel>
        <RailPanel id="planned" label="Planned next" defaultHidden>
          <FeedbackOptionalPanel kind="planned" />
        </RailPanel>
      </RailGroup>
    ),
    [requireAuth],
  );
  useRightRail(rail);

  function openSubmit() {
    if (!requireAuth('share feedback')) return;
    setSubmitOpen(true);
  }

  function handleUpvote(id: string) {
    if (!requireAuth('upvote feedback')) return;
    // Optimistic flip already happened inside toggleUpvote; a rejection has
    // already rolled the store back by the time we get here — just tell the
    // user honestly rather than leaving a silent unhandled rejection.
    toggleUpvote(id).catch(() => toast('Could not update your vote. Please try again.'));
  }

  const loading = loadState === 'loading' || loadState === 'idle';

  return (
    <section>
      <FeedbackBanner
        onSubmit={openSubmit}
        onViewAll={() => {
          setParams({}, { replace: true });
          document.getElementById('feedback-board-list')?.scrollIntoView({ block: 'start' });
        }}
      />
      {!isAuthenticated && (
        <BannerSlot>
          <GuestBanner message="You're browsing feedback on TruXSocial. Sign in to post or upvote." />
        </BannerSlot>
      )}

      <FeedbackStatsRow stats={stats} loading={statsLoading} />

      {/* Filter buttons, not ARIA tabs: selecting one filters the list below
          in place rather than swapping in a distinct tabpanel, so role="tab"
          (which promises exactly that) would be a false contract. Plain
          toggle buttons + aria-pressed, matching the Status/Sort controls
          right below (2026-08-10 adversarial review, item 14). All / My
          feedback / Bugs / Feature requests / Performance / UI-UX / More —
          the remaining categories (Accessibility, Moderation policy, Other)
          live in the More dropdown (task spec §7). */}
      <Tabs id="feedback-board-list" role="group" aria-label="Filter feedback by category">
        <Tab
          type="button"
          $on={tab === 'all'}
          aria-pressed={tab === 'all'}
          onClick={() => selectTab('all')}
        >
          All <TabCount>{formatListCount(countsItems.length)}</TabCount>
        </Tab>
        <Tab
          type="button"
          $on={tab === 'mine'}
          aria-pressed={tab === 'mine'}
          onClick={() => selectTab('mine')}
        >
          My feedback{' '}
          <TabCount>
            {countsItems.length >= FEEDBACK_LIST_CAP ? `${mineCount}+` : mineCount}
          </TabCount>
        </Tab>
        {TOP_LEVEL_CATEGORIES.map((c) => (
          <Tab
            key={c}
            type="button"
            $on={tab === c}
            aria-pressed={tab === c}
            onClick={() => selectTab(c)}
          >
            <Icon name={CATEGORY_ICONS[c]} size={12} />
            {FEEDBACK_CATEGORY_LABELS[c]}{' '}
            {/* Once the underlying (unfiltered-by-category) read hit the cap,
                no per-category count can be proven exact either — every tab
                shows "+" together, never some exact and some not. */}
            <TabCount>
              {countsItems.length >= FEEDBACK_LIST_CAP
                ? `${categoryCounts[c] ?? 0}+`
                : (categoryCounts[c] ?? 0)}
            </TabCount>
          </Tab>
        ))}
        <MoreSelect
          aria-label="More categories"
          $on={MORE_CATEGORIES.includes(tab as FeedbackCategory)}
          value={MORE_CATEGORIES.includes(tab as FeedbackCategory) ? tab : ''}
          onChange={(e) => {
            const value = e.target.value;
            if (value) selectTab(value as FeedbackCategory);
          }}
        >
          <option value="">More</option>
          {MORE_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {FEEDBACK_CATEGORY_LABELS[c]}
            </option>
          ))}
        </MoreSelect>
      </Tabs>

      <Filters>
        <Select
          aria-label="Filter by status"
          value={status}
          onChange={(e) => setFilter('status', e.target.value)}
        >
          <option value="all">All statuses</option>
          {Object.entries(FEEDBACK_STATUS_LABELS).map(([id, label]) => (
            <option key={id} value={id}>
              {label}
            </option>
          ))}
        </Select>

        <Select
          aria-label="Sort feedback"
          value={sort}
          onChange={(e) => setFilter('sort', e.target.value)}
        >
          <option value="top">Most votes</option>
          <option value="new">Newest</option>
          <option value="updated">Recently updated</option>
        </Select>

        <Spacer />
        <Link to="/feedback/roadmap">View the roadmap</Link>
      </Filters>

      {loading ? (
        <List aria-busy="true" data-testid="feedback-loading">
          <Skeleton />
          <Skeleton />
          <Skeleton />
        </List>
      ) : loadState === 'error' ? (
        <Empty role="alert">Could not load feedback. Please refresh.</Empty>
      ) : displayedItems.length === 0 ? (
        <Empty>
          {tab === 'mine'
            ? "You haven't posted any feedback yet."
            : tab === 'all' && status === 'all'
              ? 'No feedback yet. Be the first to share something.'
              : 'No feedback matches these filters.'}
        </Empty>
      ) : (
        <List>
          {displayedItems.map((item) => (
            // Participant avatars come straight from the list payload's own
            // `participants` field (FeedbackItemListSerializer.get_participants
            // — up to 3 distinct recent public commenters, prefetched
            // server-side) — no extra per-card request needed.
            <FeedbackCard key={item.id} item={item} onUpvote={handleUpvote} />
          ))}
        </List>
      )}

      <SubmitFeedbackPanel
        initialCategory={initialCategory}
        initialTitle={prefillTitle}
        initialBody={prefillBody}
        open={submitOpen}
        onClose={() => setSubmitOpen(false)}
        onSubmit={submit}
        onReconcileAfterCreate={reconcileAfterCreate}
      />
    </section>
  );
}
